import { useEffect, useMemo, useState } from 'react';

import { WireTransferApproverModalContainer } from 'common/components/WireTransferModal';
import { useFeature } from 'common/hooks/useFeature';
import { useCompanyCurrency } from 'modules/app/hooks/useCompanyCurrency';
import { NotificationType, useNotifications } from 'modules/app/notifications';
import { type QueryError } from 'src/core/api/queryError';
import {
  WireTransferConfirmationErrorModal,
  type ConfirmationError as ErrorModalConfirmationError,
} from 'src/core/common/components/WireTransferModal/WireTransferConfirmationErrorModal';
import FEATURES from 'src/core/constants/features';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';

import { useConfirmModal } from './useConfirmModal';
import { useMapErrorsToPayments } from './useMapErrorsToPayments';
import {
  type AuthType,
  useConfirmTransfersTrackingEvents,
  useConfirmTransferErrorMessage,
  useConfirmTransfers,
  useInvalidateTransferQueries,
  useQuotes,
} from '../../hooks';
import {
  GBP_BATCH_CONFIRMATION_NOT_ALLOWED_REASON,
  GBP_BATCH_CONFIRMATION_MAX_RETRIES,
  type ConfirmationError,
  type ConfirmTransferErrors,
  type VopMappingByTransferId,
} from '../../hooks/api/useConfirmTransfers';
import type { Transfer } from '../../models';

export type TransferConfirmationModalProps = {
  transfers: Transfer[];
  isOpen: boolean;
  isError?: boolean;
  isLoading?: boolean;
  onRetry?: () => void;
  onClose: () => void;
  onConfirm?: () => void;
  onError?: () => void;
};

export const TransferConfirmationModal = ({
  transfers,
  isOpen,
  isError = false,
  isLoading = false,
  onRetry,
  onClose,
  onConfirm,
  onError,
}: TransferConfirmationModalProps) => {
  const confirmTransfersTrackingEvents = useConfirmTransfersTrackingEvents();
  const { pushNotif } = useNotifications();
  const {
    getMessage: getConfirmTransferErrorMessage,
    shouldDisplayErrorNotification,
  } = useConfirmTransferErrorMessage();
  const [
    confirmTransfers,
    confirmTransfersQueryState,
    resetConfirmTransfersQueryState,
  ] = useConfirmTransfers();
  const [alternateOperationId, setAlternateOperationId] = useState<string>();

  const companyId = useCompanyId();

  const companyCurrency = useCompanyCurrency();

  const isVerificationOfPayeeEnabled = useFeature(
    FEATURES.TMP_VERIFICATION_OF_PAYEE_EEA,
  );

  const { transferIds, vopMappingByTransferId } = useMemo(() => {
    const ids = transfers.map((transfer) => transfer.id);
    const vopMapping: VopMappingByTransferId = {};

    if (isVerificationOfPayeeEnabled) {
      transfers.forEach((transfer) => {
        const verificationOfPayeeId =
          transfer.verificationOfPayee?.initialResult?.id;
        if (verificationOfPayeeId) {
          vopMapping[transfer.id] = verificationOfPayeeId;
        }
      });
    }

    return { transferIds: ids, vopMappingByTransferId: vopMapping };
  }, [transfers, isVerificationOfPayeeEnabled]);

  const invalidateTransferQueries = useInvalidateTransferQueries();

  const { quotes, totalAmountWithQuotes } = useQuotes({
    companyId,
    selectedTransfers: transfers,
    companyCurrency,
  });

  const [isApproving, setIsApproving] = useState<boolean>(false);
  const [approvalAuthType, setApprovalAuthType] = useState<AuthType | null>(
    null,
  );

  const [paymentsInError, setPaymentsInError] = useState<
    ErrorModalConfirmationError[]
  >([]);
  const { showConfirmationErrors } = useConfirmModal();

  useEffect(() => {
    if (isOpen) {
      resetConfirmTransfersQueryState();
    }
  }, [isOpen]);

  const mapErrorsToPayments = useMapErrorsToPayments();

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const onApproveTransfers = async (selectedFactor?: 'totp' | 'mfa') => {
    /**
     * In case of UK, we retry the confirmation a couple of times in case of "Transfer not found" error. This is because Adyen sometimes takes a bit of time to process the transfers.
     */
    let confirmationRetryCount = 0;
    setIsApproving(true);
    while (confirmationRetryCount < GBP_BATCH_CONFIRMATION_MAX_RETRIES) {
      try {
        confirmationRetryCount += 1;
        const data = await confirmTransfers({
          transferIds,
          vopMappingByTransferId,
          quotes,
          authType: selectedFactor,
          endpointParams: {
            shouldCallV2: true,
          },
        });

        if (quotes?.length !== 0) {
          localStorage.setItem('first-iwt-done', 'true');
        }

        if (data.procedureId) {
          setAlternateOperationId(data.procedureId);
        }
        if (data.authType) {
          setApprovalAuthType(data.authType);
        }
        return;
      } catch (error) {
        const isGbpConfirmationError =
          error.data.reason &&
          error.data.reason === GBP_BATCH_CONFIRMATION_NOT_ALLOWED_REASON &&
          error.data.errors &&
          onError;

        if (!isGbpConfirmationError) {
          if (
            error.data.reason === 'userAuthenticationFactorsNotFound' &&
            !approvalAuthType
          ) {
            if (!approvalAuthType) {
              setApprovalAuthType('mfa');
            }
            return;
          }

          displayErrorToast(error);
          setIsApproving(false);
          throw error;
        }

        const retriableError = error.data.errors?.find(
          (paymentError: ConfirmationError) =>
            paymentError?.error === 'transferNotFound',
        );

        if (
          retriableError &&
          confirmationRetryCount < GBP_BATCH_CONFIRMATION_MAX_RETRIES
        ) {
          await new Promise((resolve) => setTimeout(resolve, 5000));
          continue;
        }

        if (confirmationRetryCount >= GBP_BATCH_CONFIRMATION_MAX_RETRIES) {
          displayErrorToast(error);
          setIsApproving(false);
          throw error;
        }

        onApproveError(error.data.errors);
        setIsApproving(false);
        return;
      }
    }
  };

  return (
    <>
      <WireTransferApproverModalContainer
        isOpen={isOpen}
        amount={totalAmountWithQuotes}
        selectedPayments={transfers.map((it) => ({
          id: it.id,
          bill: it.bill,
          amount: it.amount,
        }))}
        isApproving={
          confirmTransfersQueryState.status === 'loading' || isApproving
        }
        isLoading={isLoading}
        approvalAuthType={approvalAuthType}
        onClose={() => {
          setIsApproving(false);
          onClose();
        }}
        onApprove={async (selectedFactor?: 'totp' | 'mfa') => {
          confirmTransfersTrackingEvents.onConfirmTransfersConfirmationModalConfirmed(
            { transfers },
          );
          await onApproveTransfers(selectedFactor);
        }}
        onComplete={() => {
          invalidateTransferQueries();
          if (onConfirm) {
            onConfirm();
          }
        }}
        billType="invoice"
        alternateOperationId={alternateOperationId}
      />

      <WireTransferConfirmationErrorModal
        onCancel={onClose}
        onRetry={onRetry && onRetryConfirmation}
        isOpen={isError}
        payments={paymentsInError}
      />
    </>
  );

  function onRetryConfirmation() {
    if (onRetry) {
      onRetry();
      onApproveTransfers();
    }
  }

  function onApproveError(payments: ConfirmationError[]) {
    setPaymentsInError(mapErrorsToPayments(payments, transfers));
    showConfirmationErrors();
    if (onError) {
      onError();
    }
  }

  function displayErrorToast(error: QueryError<ConfirmTransferErrors>) {
    const translationParams = {
      count: transferIds.length,
    };
    if (shouldDisplayErrorNotification(error, translationParams)) {
      pushNotif({
        type: NotificationType.Danger,
        message: getConfirmTransferErrorMessage(error, translationParams),
      });
    }
  }
};
